import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { SuperAdminLayout } from './SuperAdminLayout';
import {
  Users, Search, Plus, Edit, Trash2, Key, X, Building2, Shield
} from 'lucide-react';

const ROLE_MAP = {
  super_admin: { labelKey: 'saUsers.roleSuperAdmin', cls: 'bg-red-500/15 text-red-400' },
  admin: { labelKey: 'saUsers.roleAdmin', cls: 'bg-[#fedd00]/15 text-[#fedd00]' },
  user: { labelKey: 'saUsers.roleUser', cls: 'bg-blue-500/15 text-blue-400' },
  technicien: { labelKey: 'saUsers.roleTechnicien', cls: 'bg-purple-500/15 text-purple-400' },
  client: { labelKey: 'saUsers.roleClient', cls: 'bg-gray-500/15 text-gray-400' },
  b2b: { labelKey: 'saUsers.roleB2b', cls: 'bg-emerald-500/15 text-emerald-400' },
};

const SuperAdminUsers = () => {
  const { api } = useAuth();
  const { t, formatDate, formatNumber } = useLanguage();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [resetModal, setResetModal] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [createForm, setCreateForm] = useState({
    username: '', email: '', password: '', role: 'user', company_id: '', telephone: ''
  });
  const [companies, setCompanies] = useState([]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (roleFilter) params.append('role', roleFilter);
      params.append('limit', '100');
      const res = await api.get(`/super-admin/users?${params}`);
      setUsers(res.data.users);
      setTotal(res.data.total);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [api, search, roleFilter]);

  const fetchCompanies = useCallback(async () => {
    try {
      const res = await api.get('/super-admin/companies?limit=200');
      setCompanies(res.data.companies);
    } catch (err) { console.error(err); }
  }, [api]);

  useEffect(() => { fetchUsers(); fetchCompanies(); }, [fetchUsers, fetchCompanies]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post('/super-admin/users', createForm);
      setShowCreate(false);
      setCreateForm({ username: '', email: '', password: '', role: 'user', company_id: '', telephone: '' });
      fetchUsers();
    } catch (err) { alert(err.response?.data?.detail || t('saUsers.error')); }
  };

  const handleResetPassword = async () => {
    if (!resetModal || newPassword.length < 4) return;
    try {
      await api.post(`/super-admin/users/${resetModal.id}/reset-password`, { new_password: newPassword });
      setResetModal(null);
      setNewPassword('');
      alert(t('saUsers.resetSuccess'));
    } catch (err) { alert(err.response?.data?.detail || t('saUsers.error')); }
  };

  const handleDelete = async (userId, username) => {
    if (!window.confirm(t('saUsers.confirmDelete', { username }))) return;
    try {
      await api.delete(`/super-admin/users/${userId}`);
      fetchUsers();
    } catch (err) { alert(err.response?.data?.detail || t('saUsers.error')); }
  };

  return (
    <SuperAdminLayout>
      <div className="space-y-4" data-testid="super-admin-users">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-xl font-bold text-white">{t('saUsers.title')}</h1>
            <p className="text-sm text-gray-500">{t('saUsers.totalUsers', { count: formatNumber(total) })}</p>
          </div>
          <button onClick={() => setShowCreate(true)} data-testid="create-user-btn" className="flex items-center gap-2 px-4 py-2.5 bg-[#009b3a] text-white rounded-lg text-sm font-medium hover:bg-[#00b344] transition-colors">
            <Plus className="w-4 h-4" /> {t('saUsers.newUser')}
          </button>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text" placeholder={t('saUsers.searchPlaceholder')} value={search}
              onChange={e => setSearch(e.target.value)} data-testid="search-users"
              className="w-full pl-10 pr-4 py-2.5 bg-[#111827] border border-white/10 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:border-[#009b3a]/40"
            />
          </div>
          <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)} className="bg-[#111827] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-gray-300 focus:outline-none" data-testid="filter-role">
            <option value="">{t('saUsers.filterAllRoles')}</option>
            <option value="super_admin">{t('saUsers.roleSuperAdmin')}</option>
            <option value="admin">{t('saUsers.roleAdmin')}</option>
            <option value="user">{t('saUsers.roleUser')}</option>
            <option value="technicien">{t('saUsers.roleTechnicien')}</option>
            <option value="client">{t('saUsers.roleClient')}</option>
            <option value="b2b">{t('saUsers.roleB2b')}</option>
          </select>
        </div>

        {/* Table */}
        <div className="bg-[#111827] border border-white/5 rounded-xl overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center p-12">
              <div className="w-6 h-6 border-2 border-[#009b3a] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : users.length === 0 ? (
            <div className="p-12 text-center">
              <Users className="w-10 h-10 text-gray-600 mx-auto mb-3" />
              <p className="text-sm text-gray-500">{t('saUsers.emptyState')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/5 text-gray-500 text-xs">
                    <th className="text-left px-4 py-3 font-medium">{t('saUsers.colUser')}</th>
                    <th className="text-left px-4 py-3 font-medium">{t('saUsers.colRole')}</th>
                    <th className="text-left px-4 py-3 font-medium">{t('saUsers.colCompany')}</th>
                    <th className="text-left px-4 py-3 font-medium">{t('saUsers.colCreatedAt')}</th>
                    <th className="text-right px-4 py-3 font-medium">{t('saUsers.colActions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {users.map(u => {
                    const role = ROLE_MAP[u.role] || { labelKey: null, cls: 'bg-gray-500/15 text-gray-400' };
                    return (
                      <tr key={u.id} className="hover:bg-white/[0.02]" data-testid={`user-row-${u.id}`}>
                        <td className="px-4 py-3">
                          <p className="text-white">{u.username}</p>
                          <p className="text-xs text-gray-500">{u.email || '-'}</p>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs ${role.cls}`}>{role.labelKey ? t(role.labelKey) : u.role}</span>
                        </td>
                        <td className="px-4 py-3 text-gray-400 text-xs">{u.company_name || '-'}</td>
                        <td className="px-4 py-3 text-gray-500 text-xs">
                          {u.created_at ? formatDate(u.created_at) : '-'}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <button onClick={() => { setResetModal(u); setNewPassword(''); }} className="p-1.5 rounded-lg hover:bg-amber-500/10 text-gray-400 hover:text-amber-400" title={t('saUsers.resetPassword')}>
                              <Key className="w-4 h-4" />
                            </button>
                            <button onClick={() => handleDelete(u.id, u.username)} className="p-1.5 rounded-lg hover:bg-red-500/10 text-gray-400 hover:text-red-400" title={t('saUsers.delete')}>
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Create User Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-[#111827] border border-white/10 rounded-2xl w-full max-w-md" data-testid="create-user-modal">
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
              <h2 className="text-base font-bold text-white">{t('saUsers.newUser')}</h2>
              <button onClick={() => setShowCreate(false)} className="p-1 rounded-lg hover:bg-white/5"><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <form onSubmit={handleCreate} className="p-5 space-y-3">
              <div>
                <label className="block text-xs text-gray-400 mb-1">{t('saUsers.labelUsername')} *</label>
                <input required value={createForm.username} onChange={e => setCreateForm(p => ({...p, username: e.target.value}))} className="w-full px-3 py-2.5 bg-[#0a0f1c] border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-[#009b3a]/40" data-testid="input-new-username" />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">{t('saUsers.labelEmail')}</label>
                <input type="email" value={createForm.email} onChange={e => setCreateForm(p => ({...p, email: e.target.value}))} className="w-full px-3 py-2.5 bg-[#0a0f1c] border border-white/10 rounded-lg text-sm text-white focus:outline-none" />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">{t('saUsers.labelPassword')} *</label>
                <input required type="password" value={createForm.password} onChange={e => setCreateForm(p => ({...p, password: e.target.value}))} className="w-full px-3 py-2.5 bg-[#0a0f1c] border border-white/10 rounded-lg text-sm text-white focus:outline-none" data-testid="input-new-password" />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">{t('saUsers.colRole')}</label>
                <select value={createForm.role} onChange={e => setCreateForm(p => ({...p, role: e.target.value}))} className="w-full px-3 py-2.5 bg-[#0a0f1c] border border-white/10 rounded-lg text-sm text-white focus:outline-none">
                  <option value="super_admin">{t('saUsers.roleSuperAdmin')}</option>
                  <option value="admin">{t('saUsers.roleAdmin')}</option>
                  <option value="user">{t('saUsers.roleUser')}</option>
                  <option value="technicien">{t('saUsers.roleTechnicien')}</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">{t('saUsers.colCompany')}</label>
                <select value={createForm.company_id} onChange={e => setCreateForm(p => ({...p, company_id: e.target.value}))} className="w-full px-3 py-2.5 bg-[#0a0f1c] border border-white/10 rounded-lg text-sm text-white focus:outline-none">
                  <option value="">{t('saUsers.companyNone')}</option>
                  {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowCreate(false)} className="px-4 py-2.5 text-sm text-gray-400">{t('saUsers.cancel')}</button>
                <button type="submit" data-testid="submit-create-user" className="px-5 py-2.5 bg-[#009b3a] text-white rounded-lg text-sm font-medium hover:bg-[#00b344]">{t('saUsers.create')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {resetModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-[#111827] border border-white/10 rounded-2xl w-full max-w-sm" data-testid="reset-password-modal">
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
              <h2 className="text-base font-bold text-white">{t('saUsers.resetPassword')}</h2>
              <button onClick={() => setResetModal(null)} className="p-1 rounded-lg hover:bg-white/5"><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-gray-400">{t('saUsers.colUser')}: <strong className="text-white">{resetModal.username}</strong></p>
              <div>
                <label className="block text-xs text-gray-400 mb-1">{t('saUsers.labelNewPassword')}</label>
                <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} className="w-full px-3 py-2.5 bg-[#0a0f1c] border border-white/10 rounded-lg text-sm text-white focus:outline-none" data-testid="input-reset-password" />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button onClick={() => setResetModal(null)} className="px-4 py-2.5 text-sm text-gray-400">{t('saUsers.cancel')}</button>
                <button onClick={handleResetPassword} data-testid="confirm-reset-password" className="px-5 py-2.5 bg-amber-500 text-black rounded-lg text-sm font-medium hover:bg-amber-400">{t('saUsers.reset')}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </SuperAdminLayout>
  );
};

export default SuperAdminUsers;
